/**
 * Pericope study popover — anchored to a clicked pericope heading in the
 * reader. Offers a one-tap path into a whole-passage AI study: the button
 * opens the chat drawer and runs the pericope study.
 */

import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { SparklesIcon } from '@components/icons';

const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT = 100;

export const PericopePopover = observer(() => {
  const prompt = bibleState.pericopeStudyPrompt;

  useEffect(() => {
    if (!prompt) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.pericope-popover')) bibleState.hidePericopeStudyPrompt();
    };
    // Delay so the opening click doesn't immediately dismiss (VerseMenu pattern).
    const timer = setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [prompt]);

  const style = useMemo<React.CSSProperties>(() => {
    if (!prompt) return {};
    const { anchorRect } = prompt;
    const showAbove = anchorRect.top > POPOVER_HEIGHT + 8;
    return {
      position: 'fixed',
      left: `${Math.max(8, Math.min(anchorRect.left, window.innerWidth - POPOVER_WIDTH - 8))}px`,
      ...(showAbove
        ? { bottom: `${window.innerHeight - anchorRect.top + 6}px` }
        : { top: `${anchorRect.bottom + 6}px` }),
      zIndex: 60,
    };
  }, [prompt]);

  if (!prompt) return null;

  return (
    <div className="pericope-popover" style={style}>
      <p className="pericope-popover-passage">{prompt.title}</p>
      <button
        type="button"
        className="pericope-popover-go"
        onClick={() => bibleState.startPericopeStudy()}
      >
        <SparklesIcon className="h-4 w-4" filled />
        Generate Commentary
      </button>
    </div>
  );
});
